import { useState, useEffect, useRef } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteNav } from "@/components/site/SiteNav";
import { Footer } from "@/components/site/Sections";
import {
  Tv2,
  Radio,
  Newspaper,
  Maximize2,
  Film,
  Cpu,
  Layers,
  Search,
  Target,
  FileText,
  Layout,
  TrendingUp,
  Share2,
  ShieldCheck,
  CheckCircle2,
  ArrowRight,
  ArrowUpRight,
  Sparkles,
  Users,
  BarChart3,
  SearchCode,
  PieChart,
  LineChart,
  Megaphone,
  Briefcase,
  Zap,
  Globe2,
  Tv,
  Server,
  Smartphone,
  ShoppingBag,
  RefreshCw,
  Filter,
  Compass,
  Calendar,
  MessageSquare,
  Shield,
  FileCheck,
  UserCheck,
  Globe,
  Check,
  ChevronRight,
  ChevronLeft,
  Grid,
  Layers3,
  Award,
  Play,
  Pause,
  Palette,
  Brush,
  PenTool,
  Video,
  Camera,
  Wand2,
  Bot,
  Code,
  Mail,
  Database,
} from "lucide-react";

export const Route = createFileRoute("/services")({
  head: () => ({
    meta: [
      { title: "Our Services — KMWAdvertising · Media Planning, Buying & Omnichannel Capabilities" },
      {
        name: "description",
        content:
          "Explore KMWA's 360° advertising solutions: Media Planning & Buying, Digital Marketing, Performance Marketing, Social Media, Research & Audits, plus detailed Traditional, Digital, Branding & Consulting capabilities.",
      },
      { property: "og:title", content: "Our Services — KMWAdvertising" },
      {
        property: "og:description",
        content:
          "Media, Marketing & Performance Solutions Designed for Growth. End-to-end advertising solutions for modern brands.",
      },
    ],
  }),
  component: ServicesPage,
});

interface ServicePillar {
  id: string;
  badge: string;
  title: string;
  tagline: string;
  desc: string;
  icon: any;
  color: string;
  accentClass: string;
  bgGlow: string;
  capabilitiesTitle: string;
  capabilities: {
    name: string;
    desc: string;
    icon: any;
  }[];
  highlights: string[];
}

interface DetailedSubService {
  id: string;
  name: string;
  tagline: string;
  desc: string;
  icon: any;
  deliverables: string[];
}

interface DetailedCategory {
  id: string;
  badge: string;
  title: string;
  tagline: string;
  desc: string;
  icon: any;
  color: string;
  bgGlow: string;
  subServices: DetailedSubService[];
}

export function ServicesPage() {
  // SECTION 1: CORE SERVICE CATEGORIES (5 PILLARS)
  const servicePillars: ServicePillar[] = [
    {
      id: "media-planning",
      badge: "Media Strategy",
      title: "Media Planning & Media Buying",
      tagline: "Effective advertising starts with smart media decisions.",
      desc: "Our media planning and buying specialists analyse audience behaviour, market trends, media consumption patterns, and competitive activity to create media strategies that maximize impact while optimising budgets.",
      icon: Tv2,
      color: "var(--glow-orange)",
      accentClass: "from-amber-500/20 via-orange-500/10 to-transparent border-amber-500/30 text-amber-400",
      bgGlow: "#f97316",
      capabilitiesTitle: "Our Media Expertise Includes:",
      capabilities: [
        { name: "Television Advertising", desc: "High-impact linear and national TV campaign placement for maximum brand awareness.", icon: Tv2 },
        { name: "Radio Advertising", desc: "Targeted audio advertising on premier FM stations and digital streaming channels.", icon: Radio },
        { name: "Print Media", desc: "Strategic placement in top-tier national newspapers, trade publications, and magazines.", icon: Newspaper },
        { name: "Outdoor Advertising (OOH)", desc: "High-visibility billboards, transit ads, digital screens, and ambient OOH networks.", icon: Maximize2 },
        { name: "Cinema Advertising", desc: "Captive audience engagement with high-definition movie theater screen placements.", icon: Film },
        { name: "Programmatic Media Buying", desc: "Real-time algorithmic bidding for hyper-targeted automated ad inventory.", icon: Cpu },
        { name: "Cross-Channel Media Planning", desc: "Unified media allocation balancing traditional reach and digital conversion.", icon: Layers },
      ],
      highlights: ["Strategic Budget Optimization", "Omnichannel Reach", "Prime Ad Placements", "Audience Analysis"],
    },
    {
      id: "digital-marketing",
      badge: "Digital Growth",
      title: "Digital Marketing",
      tagline: "Digital marketing is more than running ads. It's about creating a connected customer journey.",
      desc: "We develop integrated digital marketing campaigns that help brands increase visibility, engage audiences, and drive measurable business results across every touchpoint.",
      icon: Search,
      color: "var(--glow-purple)",
      accentClass: "from-purple-500/20 via-indigo-500/10 to-transparent border-purple-500/30 text-purple-400",
      bgGlow: "#a855f7",
      capabilitiesTitle: "Services Include:",
      capabilities: [
        { name: "Search Engine Optimisation (SEO)", desc: "Technical, on-page, and off-page SEO strategies to dominate organic search rankings.", icon: SearchCode },
        { name: "Search Engine Marketing (SEM)", desc: "High-intent paid search campaigns across Google and Bing driving instant leads.", icon: Search },
        { name: "Display Advertising", desc: "Visually arresting banner campaigns across high-traffic global publisher sites.", icon: Layout },
        { name: "Content Marketing", desc: "Strategic narrative creation, articles, video scripts, and branded collateral.", icon: FileText },
        { name: "Website Strategy", desc: "UX/UI optimization and conversion-focused web architecture aligned with brand goals.", icon: Globe2 },
        { name: "Conversion Optimization", desc: "A/B testing, heatmaps, and funnel refinement to turn website traffic into customers.", icon: TrendingUp },
        { name: "OTT Platforms", desc: "Precision video advertising across CTV streaming networks like Hulu, Netflix, and Disney+.", icon: Tv },
        { name: "DSP & Programmatic Ads", desc: "Demand-Side Platform management for unified data-driven ad buying.", icon: Server },
        { name: "Social Media Management", desc: "End-to-end management of social channels with brand-aligned storytelling.", icon: Share2 },
        { name: "Online Reputation Management", desc: "Active monitoring, sentiment analysis, and reputation safeguard for executive and corporate brands.", icon: ShieldCheck },
      ],
      highlights: ["Full Customer Journey", "Organic & Paid Synergy", "Data Analytics", "Omni-channel Touchpoints"],
    },
    {
      id: "performance-marketing",
      badge: "ROI & Scale",
      title: "Performance Marketing",
      tagline: "Every click, impression, and conversion should contribute to business growth.",
      desc: "Our performance marketing team focuses on creating scalable campaigns that deliver measurable returns through continuous testing, optimisation, and audience refinement.",
      icon: TrendingUp,
      color: "var(--glow-cyan)",
      accentClass: "from-cyan-500/20 via-blue-500/10 to-transparent border-cyan-500/30 text-cyan-400",
      bgGlow: "#06b6d4",
      capabilitiesTitle: "Key Solutions:",
      capabilities: [
        { name: "Lead Generation Campaigns", desc: "High-intent customer acquisition funnels generating qualified, sales-ready prospects.", icon: Target },
        { name: "User Acquisition", desc: "Scalable growth engines engineered to acquire high-value active users at low CAC.", icon: Users },
        { name: "App Promotion", desc: "Mobile app install drives with in-app event tracking and store optimization.", icon: Smartphone },
        { name: "E-commerce Growth Campaigns", desc: "ROAS-driven shopping campaigns, dynamic retargeting, and cart recovery.", icon: ShoppingBag },
        { name: "Customer Retargeting", desc: "Precision re-engagement strategies capturing lost leads and driving lifetime value.", icon: RefreshCw },
        { name: "Funnel Optimization", desc: "Granular funnel analysis removing friction points to maximize conversion rates.", icon: Filter },
      ],
      highlights: ["Pay-for-Performance Focus", "CAC Minimization", "Continuous A/B Testing", "Scalable ROAS"],
    },
    {
      id: "social-media",
      badge: "Engagement & Community",
      title: "Social Media Marketing",
      tagline: "Consumers spend significant time on social platforms. We help brands turn attention into action.",
      desc: "We build social presence, produce high-converting creative assets, manage communities, and scale paid social advertising campaigns that build lasting brand equity.",
      icon: Share2,
      color: "var(--glow-orange)",
      accentClass: "from-pink-500/20 via-rose-500/10 to-transparent border-pink-500/30 text-pink-400",
      bgGlow: "#ec4899",
      capabilitiesTitle: "Services Include:",
      capabilities: [
        { name: "Social Media Strategy", desc: "Data-informed social roadmaps tailored for Instagram, TikTok, LinkedIn, and YouTube.", icon: Compass },
        { name: "Content Planning", desc: "Editorial calendars, creative asset creation, motion graphics, and short-form video.", icon: Calendar },
        { name: "Community Management", desc: "Active community engagement, comment response, and loyal follower nurturing.", icon: MessageSquare },
        { name: "Paid Social Campaigns", desc: "Hyper-targeted ad campaigns on Meta, TikTok, LinkedIn, and X with custom lookalikes.", icon: Megaphone },
        { name: "Influencer Partnerships", desc: "End-to-end creator strategy, vetting, negotiation, and campaign execution.", icon: Sparkles },
        { name: "Reputation Management", desc: "Proactive social listening, crisis mitigation, and brand safety protocols.", icon: Shield },
      ],
      highlights: ["Viral Storytelling", "High-Converting Creative", "Creator Networks", "Community Trust"],
    },
    {
      id: "research-analytics",
      badge: "Data & Intelligence",
      title: "Research, Analytics & Media Audits",
      tagline: "The best decisions are informed decisions.",
      desc: "Our research and analytics services help brands understand market opportunities, campaign performance, audience behaviour, and competitive positioning.",
      icon: BarChart3,
      color: "var(--glow-purple)",
      accentClass: "from-emerald-500/20 via-teal-500/10 to-transparent border-emerald-500/30 text-emerald-400",
      bgGlow: "#10b981",
      capabilitiesTitle: "Services Include:",
      capabilities: [
        { name: "Media Audits", desc: "Comprehensive independent analysis of your media buying efficiency and waste reduction.", icon: FileCheck },
        { name: "Campaign Analysis", desc: "Deep-dive attribution, multi-touch conversion modeling, and channel performance.", icon: PieChart },
        { name: "Competitor Benchmarking", desc: "In-depth audit of competitor ad spend, creative tactics, and market share.", icon: BarChart3 },
        { name: "Consumer Research", desc: "Quantitative and qualitative consumer insights revealing purchasing triggers.", icon: UserCheck },
        { name: "Market Intelligence", desc: "Macro trends, emerging media shifts, and sector growth forecasting.", icon: Globe },
        { name: "Performance Reporting", desc: "Transparent, real-time executive dashboards highlighting business impact.", icon: LineChart },
      ],
      highlights: ["Independent Auditing", "Competitor Intelligence", "Custom Dashboards", "Actionable Insights"],
    },
  ];

  // SECTION 2: SEPARATE DETAILED BREAKDOWN TABS (4 CATEGORIES)
  const detailedCategories: DetailedCategory[] = [
    {
      id: "traditional-media-detailed",
      badge: "Broadcast & Real-World",
      title: "Traditional Media",
      tagline: "Mass reach, credibility, and brand authority across Television, Radio, Print, Cinema, and Outdoor.",
      desc: "Connect with millions through prime broadcast television, targeted audio, national press publications, captive cinema screens, and high-footfall outdoor networks.",
      icon: Tv2,
      color: "var(--glow-orange)",
      bgGlow: "#f97316",
      subServices: [
        {
          id: "tv-detailed",
          name: "Television",
          tagline: "Television Advertising Services That Maximise Brand Reach, Awareness & Growth",
          desc: "Linear TV, national news channels, regional broadcasts, festive brand initiatives, and prime-time spot buying for mass demographic reach.",
          icon: Tv2,
          deliverables: ["National & Regional TV Ads", "Prime-Time Spot Buying", "Sponsorship Integrations", "Commercial Production"],
        },
        {
          id: "radio-detailed",
          name: "Radio",
          tagline: "Targeted Audio Advertising across Premier FM & Streaming Channels",
          desc: "Strategic FM spots, sponsored RJ mentions, audio jingle production, and digital audio streaming placements driving high local frequency.",
          icon: Radio,
          deliverables: ["FM Commercial Spots", "RJ Mention Sponsorships", "Audio Jingle Production", "Digital Audio & Podcast Ads"],
        },
        {
          id: "print-detailed",
          name: "Print",
          tagline: "Strategic Placements in Leading Newspapers & Trade Magazines",
          desc: "Full-page display ads, front-page wraps, advertorials, and corporate insertions in top-tier national dailies and magazines.",
          icon: Newspaper,
          deliverables: ["National Daily Wraps", "Magazines & Journals", "Advertorial Articles", "Regional Print Insertion"],
        },
        {
          id: "cinema-detailed",
          name: "Cinema Advertising",
          tagline: "High-Definition Theater Screen Commercials for Captive Audiences",
          desc: "Pre-show and interval cinema commercials, lobby activations, ticket branding, and multiplex network sponsorships across top multiplex chains.",
          icon: Film,
          deliverables: ["Multiplex HD Screen Ads", "Pre-Show & Interval Spots", "Lobby Branding & Kiosks", "Regional Screen Targeting"],
        },
        {
          id: "ooh-detailed",
          name: "Outdoor (OOH) & Transit Media",
          tagline: "High-Visibility Billboards, Transit Ads, and Digital OOH Networks",
          desc: "Billboards, digital OOH (DOOH) screens, airport branding, metro pillar wraps, and transit advertising engineered for maximum real-world impression delivery.",
          icon: Maximize2,
          deliverables: ["Billboards & Unipoles", "Digital OOH (DOOH)", "Airport & Airline Branding", "Metro, Bus & Transit Wraps"],
        },
      ],
    },
    {
      id: "digital-media-detailed",
      badge: "Performance & Tech",
      title: "Digital Media",
      tagline: "Data-Driven Digital Engines across Performance, SEO, OTT, Programmatic, ORM, and AI.",
      desc: "Deploy hyper-targeted digital campaigns that optimize customer acquisition costs, drive organic search rankings, and build omnichannel brand momentum.",
      icon: Search,
      color: "var(--glow-purple)",
      bgGlow: "#a855f7",
      subServices: [
        {
          id: "performance-detailed",
          name: "Performance Marketing (Google Ads, Meta Ads, LinkedIn Ads, etc)",
          tagline: "ROAS-Driven Customer Acquisition Funnels",
          desc: "Scalable paid search, Meta retargeting, LinkedIn B2B lead engines, and hyper-segmented audience campaigns optimized for sales ROI.",
          icon: Target,
          deliverables: ["Google PPC & Shopping Ads", "Meta Paid Social Funnels", "LinkedIn B2B Lead Gen", "CAC Minimization & ROAS Scale"],
        },
        {
          id: "display-native-detailed",
          name: "Display, Native, Rich Media Campaigns",
          tagline: "Visually Arresting Banners & Interactive Ad Placements",
          desc: "Programmatic display banners, editorial native placements, and interactive rich media ads delivered across high-traffic global publisher networks.",
          icon: Layout,
          deliverables: ["High-Impact Banners", "Native Editorial Ads", "Interactive Rich Media", "Global Publisher Placements"],
        },
        {
          id: "ott-detailed",
          name: "OTT Advertising",
          tagline: "Non-Skippable Video Placement on Connected TV (CTV)",
          desc: "Precision video advertising across CTV streaming networks like Hulu, Netflix, Hotstar, and premium video apps.",
          icon: Tv,
          deliverables: ["CTV Connected TV Ads", "OTT App Commercials", "Targeted Streaming Networks", "Non-Skippable Video Ads"],
        },
        {
          id: "app-detailed",
          name: "App Advertising",
          tagline: "Mobile App Installs & In-App Event Optimization",
          desc: "User acquisition campaigns, app store optimization (ASO), in-app event tracking, and retargeting driving high LTV app users.",
          icon: Smartphone,
          deliverables: ["App Install Drives", "App Store Optimization (ASO)", "In-App Conversion Tracking", "Mobile User Retargeting"],
        },
        {
          id: "programmatic-detailed",
          name: "Programmatic Advertising",
          tagline: "Real-Time Algorithmic Bidding & Automated Audience Buying",
          desc: "Demand-side platform (DSP) management leveraging algorithmic bidding for automated audience targeting across global ad exchanges.",
          icon: Server,
          deliverables: ["DSP Platform Management", "Real-Time Algorithmic Bidding", "First-Party Data Targeting", "Cross-Device Frequency Control"],
        },
        {
          id: "seo-detailed",
          name: "SEO",
          tagline: "Technical, On-Page & Off-Page Organic Search Dominance",
          desc: "Technical SEO audits, keyword research, on-page optimization, content strategy, and authority link building for sustainable search rankings.",
          icon: SearchCode,
          deliverables: ["Technical SEO Architecture", "Keyword Mapping", "Content Optimization", "Authority Link Building"],
        },
        {
          id: "orm-detailed",
          name: "Online Reputation Management (ORM)",
          tagline: "Proactive Sentiment Monitoring & Executive PR Safeguards",
          desc: "Continuous brand sentiment tracking, review management, negative content suppression, and corporate reputation safeguarding.",
          icon: ShieldCheck,
          deliverables: ["Sentiment Analysis", "Review Management", "Search Result Suppression", "Executive PR Shield"],
        },
        {
          id: "social-detailed",
          name: "Social Media Marketing",
          tagline: "Viral Storytelling, Paid Social & Creator Partnerships",
          desc: "Social media strategy, content creation, community management, creator partnerships, and paid social campaigns for Meta, TikTok, and YouTube.",
          icon: Share2,
          deliverables: ["Channel Strategy", "Creative Asset Creation", "Creator & Influencer Outreach", "Community Management"],
        },
        {
          id: "ai-marketing-detailed",
          name: "AI Marketing",
          tagline: "Predictive Analytics & AI-Driven Automated Campaign Scaling",
          desc: "Leveraging generative AI and predictive machine learning to hyper-personalize messaging, automate ad creative variations, and forecast performance.",
          icon: Bot,
          deliverables: ["Predictive Analytics", "Generative AI Ad Assets", "Automated Ad Testing", "Personalization Engines"],
        },
        {
          id: "web-design-detailed",
          name: "Website Design, UI/UX Design",
          tagline: "User-Centric Architecture Engineered for Conversion",
          desc: "User-centric UI/UX design, wireframing, interactive prototyping, and brand-aligned design systems engineered to convert visitors into customers.",
          icon: Palette,
          deliverables: ["UX Audits & Research", "UI Interface Design", "Wireframing & Prototypes", "Design Systems"],
        },
        {
          id: "web-dev-detailed",
          name: "Web Development & Landing Pages",
          tagline: "Ultra-Fast, High-Converting Custom Web Architecture",
          desc: "Custom web development using modern frameworks, headless CMS, landing page optimization, and ultra-fast page speed performance.",
          icon: Code,
          deliverables: ["Full-Stack Web Development", "Landing Page Funnels", "Headless CMS Integration", "Core Web Vitals Speed"],
        },
        {
          id: "automation-detailed",
          name: "Marketing Automation",
          tagline: "Automated Multi-Channel Nurture Sequences & Lead Scoring",
          desc: "Building automated marketing workflows, lead scoring models, triggered messages, and multi-channel drip sequences.",
          icon: Zap,
          deliverables: ["Automated Drip Sequences", "Lead Scoring & Grading", "Multi-Channel Triggers", "Lifecycle Marketing Funnels"],
        },
        {
          id: "crm-detailed",
          name: "CRM Marketing",
          tagline: "Salesforce, HubSpot & Zoho Retention Optimization",
          desc: "Salesforce, HubSpot, and Zoho CRM integration, customer segmentation, retention strategies, and lifetime value optimization.",
          icon: Database,
          deliverables: ["CRM Setup & Integration", "Audience Segmentation", "Retention Strategy", "Customer Lifetime Value (LTV)"],
        },
        {
          id: "email-detailed",
          name: "Email Marketing",
          tagline: "High-Deliverability Newsletters & Drip Campaigns",
          desc: "Strategic newsletter design, transactional email setup, inbox deliverability optimization, and personalized dynamic email campaigns.",
          icon: Mail,
          deliverables: ["Template Design & Copy", "Deliverability Optimization", "Behavioral Workflows", "A/B Subject Testing"],
        },
        {
          id: "analytics-detailed",
          name: "Analytics",
          tagline: "Multi-Touch Attribution Modeling & Real-Time Reporting",
          desc: "Google Analytics 4 (GA4) configuration, server-side tagging, custom dashboard creation, and cross-channel attribution modeling.",
          icon: BarChart3,
          deliverables: ["GA4 & Server-Side Tagging", "Executive Dashboards", "Multi-Touch Attribution", "Funnel Friction Mapping"],
        },
        {
          id: "cro-detailed",
          name: "Conversion Optimisation (CRO)",
          tagline: "A/B Testing, Heatmaps & Funnel Friction Reduction",
          desc: "A/B multivariate testing, user session recordings, heatmaps, and landing page UX refinements to maximize conversion rates.",
          icon: TrendingUp,
          deliverables: ["A/B & Multivariate Testing", "Heatmaps & Replays", "Funnel Conversion Audits", "Checkout Optimization"],
        },
        {
          id: "data-intel-detailed",
          name: "Data Intelligence",
          tagline: "First-Party Data Strategy & Predictive Market Forecasting",
          desc: "Unlocking first-party customer data, predictive market trend forecasting, audience persona clustering, and actionable growth insights.",
          icon: PieChart,
          deliverables: ["First-Party Data Setup", "Predictive Trend Analysis", "Audience Persona Clustering", "Market Share Forecasting"],
        },
      ],
    },
    {
      id: "branding-creatives-detailed",
      badge: "Creative & Identity",
      title: "Branding & Creatives",
      tagline: "Enduring Visual Identities, Motion Graphics, AI Video & Commercial Production.",
      desc: "Build strong brand equity through strategic positioning, logo design, visual style guides, motion design, video filming, photography, and copywriting.",
      icon: Sparkles,
      color: "var(--glow-orange)",
      bgGlow: "#ec4899",
      subServices: [
        {
          id: "branding-sub",
          name: "Branding",
          tagline: "Strategic Brand Positioning & Core Identity Playbooks",
          desc: "Comprehensive brand strategy, brand story development, positioning frameworks, value propositions, and brand architecture.",
          icon: Sparkles,
          deliverables: ["Brand Strategy & Positioning", "Brand Voice & Storytelling", "Brand Architecture", "Brand Guidelines Playbook"],
        },
        {
          id: "logo-sub",
          name: "Logo Design",
          tagline: "Iconic, Memorable & Vector-Perfect Logo Concepts",
          desc: "Custom logo concept creation, vector wordmarks, iconic emblems, and versatile brand mark suites built for digital and print scale.",
          icon: PenTool,
          deliverables: ["Custom Logo Concepts", "Vector Graphic Files", "Brand Mark Variations", "Monogram & Wordmark Design"],
        },
        {
          id: "visual-id-sub",
          name: "Visual Identity",
          tagline: "Complete Visual Design Systems & Guidelines",
          desc: "Typography selection, curated color palettes, custom iconography, pattern libraries, and comprehensive visual style guides.",
          icon: Brush,
          deliverables: ["Color Palette System", "Typography Hierarchy", "Iconography & Patterns", "Brand Asset Library"],
        },
        {
          id: "motion-sub",
          name: "Motion Graphics",
          tagline: "Dynamic 2D/3D Animation & Explainer Videos",
          desc: "Animated logo reveals, 2D/3D motion graphics, product explainer videos, social video animations, and promotional motion design.",
          icon: Zap,
          deliverables: ["Animated Logo Reveals", "2D/3D Motion Design", "Explainer Animations", "Social Media Motion Assets"],
        },
        {
          id: "ai-video-sub",
          name: "AI Video Creation (Long-form & Short-form)",
          tagline: "Fast, AI-Driven Video Synthesis for Social & Ads",
          desc: "Script-to-video AI synthesis, automated Reel/TikTok clip generation, AI voiceover integration, and rapid short-form video scaling.",
          icon: Wand2,
          deliverables: ["Script-to-Video AI", "Automated Reel/TikTok Clips", "AI Voiceovers & Subtitles", "Rapid Short-Form Content"],
        },
        {
          id: "video-prod-sub",
          name: "Video Production",
          tagline: "Cinematic Filming & Post-Production Commercials",
          desc: "Full commercial video production, scriptwriting, location shoots, high-definition filming, color grading, sound design, and editing.",
          icon: Video,
          deliverables: ["TV Commercial Filming", "Corporate Brand Videos", "Color Grading & Editing", "Sound Design & Scoring"],
        },
        {
          id: "photo-sub",
          name: "Photography",
          tagline: "Professional Product, Corporate & Lifestyle Shoots",
          desc: "High-end product photography, executive headshots, brand lifestyle imagery, e-commerce catalog shoots, and campaign photo assets.",
          icon: Camera,
          deliverables: ["Studio Product Photography", "Corporate Headshots", "Lifestyle Brand Shoots", "High-Res Editing"],
        },
        {
          id: "copywriting-sub",
          name: "Copywriting",
          tagline: "Persuasive Direct-Response Copy That Drives Action",
          desc: "High-converting ad copy, landing page headlines, brand scripts, email campaigns, magazine articles, and social media copy.",
          icon: FileText,
          deliverables: ["Direct-Response Ad Copy", "Landing Page Copywriting", "Commercial Video Scripts", "Brand Taglines & Slogans"],
        },
        {
          id: "campaign-design-sub",
          name: "Campaign Design",
          tagline: "360° Key Visual (KV) & Omnichannel Creative Suites",
          desc: "Master key visual creation, multi-format ad assets, billboard graphics, social ad suites, and retail collateral design.",
          icon: Layout,
          deliverables: ["Master Key Visuals (KV)", "Omnichannel Creative Suites", "Print & OOH Collateral", "Digital Banner Formats"],
        },
      ],
    },
    {
      id: "research-consulting-detailed",
      badge: "Audits & Strategy",
      title: "Research & Consulting",
      tagline: "Independent Audits, Rate Benchmarking & Strategic Growth Roadmaps.",
      desc: "Protect ad budgets and verify performance efficiency with independent media, brand health, and attribution audits.",
      icon: BarChart3,
      color: "var(--glow-purple)",
      bgGlow: "#10b981",
      subServices: [
        {
          id: "media-audit-sub",
          name: "Media Audit",
          tagline: "Independent Buying Efficiency & Rate Benchmarking",
          desc: "Comprehensive review of historical media spend, rate card benchmarking, ad placement waste detection, and agency contract verification.",
          icon: FileCheck,
          deliverables: ["Media Buying Efficiency Review", "Rate Card Benchmarking", "Ad Spend Waste Reduction", "Contract & Placement Verification"],
        },
        {
          id: "brand-audit-sub",
          name: "Brand Audit",
          tagline: "360° Brand Health & Competitor Positioning Review",
          desc: "Evaluating brand perception, competitive messaging, customer touchpoint consistency, and market positioning opportunities.",
          icon: UserCheck,
          deliverables: ["Brand Perception Analysis", "Competitor Messaging Audit", "Touchpoint Consistency Review", "Market Opportunity Mapping"],
        },
        {
          id: "campaign-audit-sub",
          name: "Campaign Audit",
          tagline: "Deep-Dive Attribution & Conversion Leak Analysis",
          desc: "Reviewing multi-touch attribution, ad creative fatigue, funnel friction points, landing page drop-offs, and ROAS optimization potential.",
          icon: PieChart,
          deliverables: ["Attribution Model Audit", "Funnel Leak Identification", "Ad Creative Fatigue Analysis", "ROAS Optimization Roadmap"],
        },
      ],
    },
  ];

  const valueProps = [
    {
      num: "01",
      title: "Integrated media and marketing expertise",
      desc: "Single-point strategic accountability across both brand-building media and direct-response performance channels.",
      icon: Layers,
    },
    {
      num: "02",
      title: "Traditional + digital capabilities under one roof",
      desc: "Seamless execution blending mass-reach TV, Radio, Print & OOH with precision SEO, SEM, Programmatic & Social.",
      icon: Tv2,
    },
    {
      num: "03",
      title: "Data-driven planning and optimization",
      desc: "Every dollar invested is guided by consumer research, market intelligence, and continuous performance testing.",
      icon: Cpu,
    },
    {
      num: "04",
      title: "Industry-specific campaign experience",
      desc: "Deep domain playbooks across Gaming, E-commerce, Real Estate, FMCG, Technology, and Financial Services.",
      icon: Briefcase,
    },
    {
      num: "05",
      title: "Transparent reporting and measurable outcomes",
      desc: "Full visibility into ad spend, impression delivery, CAC, ROAS, and tangible bottom-line growth metrics.",
      icon: BarChart3,
    },
  ];

  // SECTION 1 State (5 Pillars)
  const [activeTabId, setActiveTabId] = useState<string>("media-planning");
  const [viewMode, setViewMode] = useState<"tabs" | "stacked">("tabs");
  const [isAutoPlaying, setIsAutoPlaying] = useState<boolean>(false);

  // SECTION 2 State (Detailed Breakdown)
  const [activeDetailCatId, setActiveDetailCatId] = useState<string>("traditional-media-detailed");
  const [activeSubServiceId, setActiveSubServiceId] = useState<string>("tv-detailed");
  const subServicesScrollRef = useRef<HTMLDivElement | null>(null);
  const [isSubScrollHovered, setIsSubScrollHovered] = useState<boolean>(false);

  // Auto-scroll Section 2 Sub-Services vertically
  useEffect(() => {
    const el = subServicesScrollRef.current;
    if (!el) return;

    let animId: number;
    let lastTime = performance.now();

    const step = (now: number) => {
      const delta = now - lastTime;
      lastTime = now;

      if (!isSubScrollHovered && el.scrollHeight > el.clientHeight) {
        // Ultra-smooth vertical scroll velocity (~0.35px per frame)
        const scrollSpeed = 0.35 * (delta / 16.6);
        el.scrollTop += scrollSpeed;

        // Reset smoothly when reaching the bottom
        if (el.scrollTop >= el.scrollHeight - el.clientHeight - 2) {
          el.scrollTop = 0;
        }
      }

      animId = requestAnimationFrame(step);
    };

    animId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(animId);
  }, [isSubScrollHovered, activeDetailCatId]);

  // Refs for tab scrolling
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const tabButtonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});

  // Sync state with URL hashtag on mount / change
  useEffect(() => {
    const handleHash = () => {
      const hash = window.location.hash.replace("#", "");
      if (hash) {
        if (servicePillars.some((sp) => sp.id === hash)) {
          setActiveTabId(hash);
        } else if (detailedCategories.some((dc) => dc.id === hash)) {
          setActiveDetailCatId(hash);
        }
      }
    };
    handleHash();
    window.addEventListener("hashchange", handleHash);
    return () => window.removeEventListener("hashchange", handleHash);
  }, []);

  // Auto-advance Section 1 Tabs timer (cycles every 4.5 seconds when ON)
  useEffect(() => {
    if (!isAutoPlaying || viewMode !== "tabs") return;

    const interval = setInterval(() => {
      setActiveTabId((prevId) => {
        const currIndex = servicePillars.findIndex((sp) => sp.id === prevId);
        const nextIndex = (currIndex + 1) % servicePillars.length;
        return servicePillars[nextIndex].id;
      });
    }, 4500);

    return () => clearInterval(interval);
  }, [isAutoPlaying, viewMode]);

  // Smooth scroll active tab button on mobile screens (<768px)
  useEffect(() => {
    if (viewMode === "tabs" && scrollContainerRef.current && tabButtonRefs.current[activeTabId]) {
      const container = scrollContainerRef.current;
      const activeBtn = tabButtonRefs.current[activeTabId];

      if (window.innerWidth >= 768) {
        container.scrollLeft = 0;
        return;
      }

      if (activeBtn) {
        const currIdx = servicePillars.findIndex((sp) => sp.id === activeTabId);
        if (currIdx === 0) {
          container.scrollTo({ left: 0, behavior: "smooth" });
        } else if (currIdx === servicePillars.length - 1) {
          container.scrollTo({ left: container.scrollWidth, behavior: "smooth" });
        } else {
          const btnOffsetLeft = activeBtn.offsetLeft;
          const btnWidth = activeBtn.offsetWidth;
          const containerWidth = container.offsetWidth;
          const targetScrollLeft = btnOffsetLeft - containerWidth / 2 + btnWidth / 2;

          container.scrollTo({
            left: Math.max(0, targetScrollLeft),
            behavior: "smooth",
          });
        }
      }
    }
  }, [activeTabId, viewMode]);

  // Manual scroll helper for left/right scroll arrows
  const handleScrollTabs = (direction: "left" | "right") => {
    const currIndex = servicePillars.findIndex((sp) => sp.id === activeTabId);
    let nextIndex: number;
    if (direction === "left") {
      nextIndex = (currIndex - 1 + servicePillars.length) % servicePillars.length;
    } else {
      nextIndex = (currIndex + 1) % servicePillars.length;
    }
    const nextPillar = servicePillars[nextIndex];
    setActiveTabId(nextPillar.id);
    if (viewMode !== "tabs") setViewMode("tabs");
    window.history.replaceState(null, "", `#${nextPillar.id}`);
  };

  const activePillar = servicePillars.find((sp) => sp.id === activeTabId) || servicePillars[0];
  const ActiveIcon = activePillar.icon;

  const activeDetailCategory =
    detailedCategories.find((dc) => dc.id === activeDetailCatId) || detailedCategories[0];
  const activeSubService =
    activeDetailCategory.subServices.find((s) => s.id === activeSubServiceId) ||
    activeDetailCategory.subServices[0];
  const ActiveSubIcon = activeSubService.icon;

  return (
    <div className="dark relative min-h-screen bg-background text-foreground flex flex-col w-full overflow-x-clip">
      <SiteNav />

      <main className="flex-1 w-full overflow-x-clip pt-24 md:pt-28">
        {/* HERO SECTION */}
        <section className="relative overflow-hidden py-14 sm:py-20 md:py-24 border-b border-white/10">
          <div className="absolute inset-0 grid-bg opacity-15" aria-hidden />
          <div className="glow-orb -left-32 top-10 h-[450px] w-[450px] bg-[var(--glow-purple)] opacity-30" />
          <div className="glow-orb right-[-100px] top-20 h-[400px] w-[400px] bg-[var(--glow-orange)] opacity-25" />

          <div className="container-x relative z-10">
            {/* Breadcrumb */}
            <div className="flex flex-wrap items-center gap-2.5 text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">
              <Link to="/" className="hover:text-white transition">
                Home
              </Link>
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
              <span className="text-[var(--glow-orange)] font-semibold">Services</span>
            </div>

            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-muted-foreground border border-white/10 mb-6">
                <span className="h-2 w-2 rounded-full bg-[var(--glow-orange)] shadow-[0_0_10px_var(--glow-orange)] animate-pulse" />
                KMWA Advertising Capabilities
              </div>

              <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.12] tracking-tight text-white">
                Media, Marketing & Performance Solutions <br className="hidden sm:inline" />
                <span className="text-gradient">Designed for Growth</span>
              </h1>

              <div className="mt-6 space-y-4 text-base sm:text-lg text-white/90 font-normal leading-relaxed">
                <p className="font-medium text-white/95 text-lg sm:text-xl leading-relaxed">
                  Today's consumers move seamlessly between digital screens, social platforms, television, search engines, and real-world experiences. Your marketing strategy should do the same.
                </p>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed max-w-2xl">
                  At KMWAdvertising, we offer end-to-end advertising solutions that help brands build awareness, drive engagement, generate leads, and increase revenue through carefully planned and expertly executed campaigns.
                </p>
              </div>
            </div>

            {/* Quick Metrics Header Stats */}
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4 pt-8 border-t border-white/10">
              <div className="p-4 rounded-2xl glass-strong border border-white/5">
                <div className="text-2xl sm:text-3xl font-bold text-white font-display">360°</div>
                <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground mt-1">Full-Funnel Capabilities</div>
              </div>
              <div className="p-4 rounded-2xl glass-strong border border-white/5">
                <div className="text-2xl sm:text-3xl font-bold text-[var(--glow-orange)] font-display">5+</div>
                <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground mt-1">Core Service Pillars</div>
              </div>
              <div className="p-4 rounded-2xl glass-strong border border-white/5">
                <div className="text-2xl sm:text-3xl font-bold text-[var(--glow-purple)] font-display">Traditional + Digital</div>
                <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground mt-1">Unified Execution</div>
              </div>
              <div className="p-4 rounded-2xl glass-strong border border-white/5">
                <div className="text-2xl sm:text-3xl font-bold text-[var(--glow-cyan)] font-display">100%</div>
                <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground mt-1">Measurable Returns</div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 1: CORE SERVICE CATEGORIES INTERACTIVE TABS */}
        <section id="services-tabs" className="py-12 sm:py-16 md:py-24 border-b border-white/5 relative">
          <div className="container-x">
            {/* View Mode Toggle Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
              <div>
                <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-orange)] flex items-center gap-2 mb-1">
                  <Sparkles className="h-3.5 w-3.5" />
                  Explore Our Solutions
                </span>
                <h2 className="font-display text-2xl sm:text-3xl font-bold text-white">
                  Service Categories
                </h2>
              </div>

              {/* View Switcher & Controls Toolbar */}
              <div className="flex flex-wrap items-center gap-2.5 self-start sm:self-auto">
                {/* Previous / Next Tab Arrows */}
                {viewMode === "tabs" && (
                  <div className="inline-flex items-center gap-1 p-1 rounded-full bg-white/[0.05] border border-white/10">
                    <button
                      onClick={() => handleScrollTabs("left")}
                      className="h-8 w-8 rounded-full flex items-center justify-center bg-white/5 hover:bg-white/15 text-white/80 hover:text-white transition cursor-pointer active:scale-95"
                      aria-label="Previous tab"
                      title="Previous tab"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleScrollTabs("right")}
                      className="h-8 w-8 rounded-full flex items-center justify-center bg-white/5 hover:bg-white/15 text-white/80 hover:text-white transition cursor-pointer active:scale-95"
                      aria-label="Next tab"
                      title="Next tab"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                )}

                {/* Auto Rotate Toggle Pill */}
                {viewMode === "tabs" && (
                  <button
                    onClick={() => setIsAutoPlaying(!isAutoPlaying)}
                    className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-mono transition duration-300 border cursor-pointer ${
                      isAutoPlaying
                        ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300 shadow-[0_0_15px_rgba(16,185,129,0.2)]"
                        : "bg-white/5 border-white/10 text-muted-foreground hover:text-white"
                    }`}
                    title={isAutoPlaying ? "Click to pause tab auto-rotation" : "Click to enable tab auto-rotation"}
                  >
                    {isAutoPlaying ? (
                      <>
                        <Pause className="h-3 w-3 text-emerald-400 animate-pulse" />
                        <span className="font-medium">Auto Movable: ON</span>
                      </>
                    ) : (
                      <>
                        <Play className="h-3 w-3 text-muted-foreground" />
                        <span className="font-medium">Auto Movable: OFF</span>
                      </>
                    )}
                  </button>
                )}

                {/* View Switcher: Interactive Tabs vs Stacked Overview */}
                <div className="inline-flex items-center gap-1 p-1 rounded-full bg-white/[0.05] border border-white/10">
                  <button
                    onClick={() => setViewMode("tabs")}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium transition duration-300 cursor-pointer ${
                      viewMode === "tabs"
                        ? "bg-white/15 text-white shadow-lg border border-white/20"
                        : "text-muted-foreground hover:text-white"
                    }`}
                  >
                    <Layers3 className="h-3.5 w-3.5" />
                    Interactive Tabs
                  </button>
                  <button
                    onClick={() => setViewMode("stacked")}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium transition duration-300 cursor-pointer ${
                      viewMode === "stacked"
                        ? "bg-white/15 text-white shadow-lg border border-white/20"
                        : "text-muted-foreground hover:text-white"
                    }`}
                  >
                    <Grid className="h-3.5 w-3.5" />
                    All Services Stacked
                  </button>
                </div>
              </div>
            </div>

            {/* TAB LIST NAVIGATION STRIP - 5 COLUMN DESKTOP GRID */}
            <div className="sticky top-20 sm:top-24 z-40 mb-10 py-2.5 sm:py-3 backdrop-blur-2xl bg-background/90 border border-white/15 rounded-2xl px-2 sm:px-3 shadow-2xl transition-all">
              <div
                ref={scrollContainerRef}
                className="flex md:grid md:grid-cols-5 items-center gap-2 sm:gap-2.5 overflow-x-auto md:overflow-visible no-scrollbar py-1 w-full scroll-smooth px-1"
              >
                {servicePillars.map((pillar) => {
                  const IconComp = pillar.icon;
                  const isActive = activeTabId === pillar.id && viewMode === "tabs";
                  return (
                    <button
                      key={pillar.id}
                      ref={(el) => {
                        tabButtonRefs.current[pillar.id] = el;
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        setActiveTabId(pillar.id);
                        if (viewMode !== "tabs") setViewMode("tabs");
                        window.history.replaceState(null, "", `#${pillar.id}`);
                      }}
                      className={`group relative flex items-center justify-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 lg:px-4 py-2.5 rounded-xl text-xs sm:text-xs md:text-xs lg:text-sm font-medium transition-all duration-300 shrink-0 md:shrink md:w-full cursor-pointer ${
                        isActive
                          ? "bg-white/15 text-white border border-white/30 shadow-[0_0_25px_rgba(255,255,255,0.15)] ring-1 ring-white/20"
                          : "bg-white/[0.03] text-muted-foreground hover:text-white hover:bg-white/[0.08] border border-white/5"
                      }`}
                    >
                      <span
                        className={`h-6 w-6 sm:h-7 sm:w-7 rounded-lg flex items-center justify-center transition duration-300 shrink-0 ${
                          isActive
                            ? "bg-white/20 text-white shadow-inner"
                            : "bg-white/5 text-muted-foreground group-hover:text-white"
                        }`}
                      >
                        <IconComp className="h-3.5 w-3.5" />
                      </span>
                      <span className="truncate">{pillar.title}</span>
                      <span
                        className={`text-[10px] px-1.5 py-0.5 rounded-full font-mono transition shrink-0 ${
                          isActive
                            ? "bg-white/25 text-white"
                            : "bg-white/5 text-muted-foreground"
                        }`}
                      >
                        {pillar.capabilities.length}
                      </span>

                      {/* Active Progress Line Indicator */}
                      {isActive && isAutoPlaying && (
                        <span className="absolute bottom-0 left-2 right-2 h-[2px] bg-gradient-to-r from-amber-400 via-orange-400 to-purple-400 rounded-full animate-[pulse_1.5s_ease-in-out_infinite]" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* VIEW MODE 1: INTERACTIVE TABBED VIEW */}
            {viewMode === "tabs" && (
              <div className="animate-[fade-up_.4s_ease-out]">
                {/* Active Tab Main Card Container */}
                <div className="rounded-3xl glass-strong border border-white/15 p-6 sm:p-10 md:p-12 relative overflow-hidden shadow-2xl">
                  {/* Subtle Background Radial Glow */}
                  <div
                    className="absolute -top-32 -right-32 h-96 w-96 rounded-full blur-[140px] opacity-20 pointer-events-none transition-all duration-700"
                    style={{ backgroundColor: activePillar.bgGlow }}
                  />

                  <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-start relative z-10">
                    {/* Left Column: Category Overview & Tagline */}
                    <div className="lg:col-span-5 space-y-6">
                      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono tracking-wider border bg-white/5 border-white/10 text-white/80">
                        <ActiveIcon className="h-3.5 w-3.5 text-[var(--glow-orange)]" />
                        {activePillar.badge}
                      </div>

                      <div>
                        <h3 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-white leading-tight">
                          {activePillar.title}
                        </h3>
                        <p className="mt-3 text-base sm:text-lg font-accent italic text-white/90 border-l-2 border-[var(--glow-orange)] pl-4 py-0.5">
                          "{activePillar.tagline}"
                        </p>
                      </div>

                      <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                        {activePillar.desc}
                      </p>

                      {/* Highlights Pill Badges */}
                      <div className="pt-2">
                        <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground block mb-3">
                          Key Strengths:
                        </span>
                        <div className="flex flex-wrap gap-2">
                          {activePillar.highlights.map((h, i) => (
                            <span
                              key={i}
                              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium bg-white/[0.05] border border-white/10 text-white/90"
                            >
                              <Check className="h-3 w-3 text-emerald-400" />
                              {h}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Action CTA for Active Tab */}
                      <div className="pt-4">
                        <a
                          href="/contact-us"
                          className="btn-hero !py-3 !px-6 !text-xs sm:!text-sm w-full sm:w-auto inline-flex items-center justify-center gap-2"
                        >
                          Book {activePillar.title.split(" ")[0]} Consultation
                          <ArrowRight className="h-4 w-4" />
                        </a>
                      </div>
                    </div>

                    {/* Right Column: Interactive Capabilities & Services List */}
                    <div className="lg:col-span-7 bg-[#06060c]/90 rounded-2xl p-6 sm:p-8 border border-white/10 shadow-inner">
                      <div className="flex items-center justify-between border-b border-white/10 pb-4 mb-6">
                        <h4 className="text-sm uppercase font-mono tracking-widest text-white/90 flex items-center gap-2">
                          <Sparkles className="h-4 w-4 text-[var(--glow-orange)]" />
                          {activePillar.capabilitiesTitle}
                        </h4>
                        <span className="text-xs font-mono text-muted-foreground">
                          {activePillar.capabilities.length} Offerings
                        </span>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-3.5">
                        {activePillar.capabilities.map((cap, cIdx) => {
                          const CapIcon = cap.icon;
                          return (
                            <div
                              key={cIdx}
                              className="p-4 rounded-xl bg-white/[0.03] border border-white/5 hover:border-white/20 hover:bg-white/[0.06] transition-all duration-300 group/cap flex flex-col justify-between"
                            >
                              <div className="flex items-start gap-3">
                                <div className="h-9 w-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0 group-hover/cap:scale-110 group-hover/cap:bg-white/10 transition-all duration-300">
                                  <CapIcon className="h-4.5 w-4.5 text-white/90" />
                                </div>
                                <div>
                                  <h5 className="text-xs sm:text-sm font-semibold text-white group-hover/cap:text-[var(--glow-orange)] transition-colors">
                                    {cap.name}
                                  </h5>
                                  <p className="text-[11px] text-muted-foreground mt-1 leading-snug line-clamp-2">
                                    {cap.desc}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* VIEW MODE 2: STACKED OVERVIEW OF ALL 5 PILLARS */}
            {viewMode === "stacked" && (
              <div className="space-y-12 animate-[fade-up_.4s_ease-out]">
                {servicePillars.map((sp, idx) => {
                  const MainIcon = sp.icon;
                  return (
                    <div
                      key={sp.id}
                      id={sp.id}
                      className="scroll-mt-32 p-8 sm:p-10 md:p-12 rounded-3xl glass-strong border border-white/10 relative overflow-hidden shadow-2xl group hover:border-white/20 transition duration-500"
                    >
                      <div
                        className="absolute -top-32 -right-32 h-64 w-64 rounded-full blur-3xl opacity-10 group-hover:opacity-20 transition-opacity"
                        style={{ backgroundColor: sp.bgGlow }}
                      />

                      <div className="grid lg:grid-cols-12 gap-8 items-start relative z-10">
                        {/* Left Column: Intro & Summary */}
                        <div className="lg:col-span-5">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="h-12 w-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center text-white">
                              <MainIcon className="h-6 w-6" style={{ color: sp.color }} />
                            </div>
                            <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                              PILLAR 0{idx + 1}
                            </span>
                          </div>

                          <h3 className="font-display text-2xl sm:text-3xl font-bold text-white leading-tight">
                            {sp.title}
                          </h3>

                          <p className="mt-3 text-sm font-semibold text-white/90 italic font-accent">
                            "{sp.tagline}"
                          </p>

                          <p className="mt-4 text-xs sm:text-sm text-muted-foreground leading-relaxed">
                            {sp.desc}
                          </p>

                          <div className="mt-6 flex flex-wrap gap-2">
                            {sp.highlights.map((h, i) => (
                              <span
                                key={i}
                                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium bg-white/[0.05] border border-white/10 text-white/80"
                              >
                                <Check className="h-3 w-3 text-emerald-400" />
                                {h}
                              </span>
                            ))}
                          </div>
                        </div>

                        {/* Right Column: Grid of Capabilities */}
                        <div className="lg:col-span-7 bg-[#06060c]/90 rounded-2xl p-6 border border-white/10">
                          <div className="text-xs uppercase font-mono tracking-widest text-muted-foreground mb-4">
                            {sp.capabilitiesTitle}
                          </div>
                          <div className="grid sm:grid-cols-2 gap-3">
                            {sp.capabilities.map((cap, cIdx) => {
                              const CIcon = cap.icon;
                              return (
                                <div
                                  key={cIdx}
                                  className="p-3.5 rounded-xl bg-white/[0.03] border border-white/5 hover:border-white/15 transition flex items-start gap-3"
                                >
                                  <div className="h-8 w-8 rounded-lg bg-white/5 flex items-center justify-center shrink-0 text-white/90">
                                    <CIcon className="h-4 w-4" />
                                  </div>
                                  <div>
                                    <h4 className="text-xs sm:text-sm font-semibold text-white">
                                      {cap.name}
                                    </h4>
                                    <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug line-clamp-2">
                                      {cap.desc}
                                    </p>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {/* SECTION 2: SEPARATE DETAILED BREAKDOWN SECTION (4 CATEGORIES + 34 SUB-SERVICES) */}
        <section id="detailed-breakdown" className="py-16 md:py-24 border-b border-white/10 relative overflow-hidden bg-white/[0.01]">
          <div className="container-x relative z-10">
            {/* Header */}
            <div className="max-w-3xl mb-10">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-orange)] block mb-2 flex items-center gap-2">
                <Sparkles className="h-3.5 w-3.5" />
                Comprehensive Channel Matrix
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
                Detailed Solutions Breakdown
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground mt-3 leading-relaxed">
                Select from our 4 specialized advertising pillars below to explore targeted execution channels—ranging from broadcast Television and Outdoor to Performance Marketing, AI Video, and Independent Audits.
              </p>
            </div>

            {/* 4 Category Pill Buttons for Section 2 */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
              {detailedCategories.map((cat) => {
                const CatIcon = cat.icon;
                const isCatActive = activeDetailCatId === cat.id;

                if (cat.id === "research-consulting-detailed") {
                  return (
                    <Link
                      key={cat.id}
                      to="/services/research-consulting"
                      className={`flex items-center justify-center gap-2.5 p-3.5 rounded-2xl border text-xs sm:text-sm font-bold transition-all duration-300 cursor-pointer ${
                        isCatActive
                          ? "bg-emerald-500/20 text-white border-emerald-500/40 shadow-[0_0_20px_rgba(16,185,129,0.3)] ring-1 ring-emerald-500/30"
                          : "bg-white/[0.03] text-muted-foreground hover:text-white hover:bg-white/[0.08] border-white/5"
                      }`}
                    >
                      <CatIcon className="h-4 w-4 text-emerald-400" />
                      <span className="truncate">{cat.title}</span>
                      <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                        {cat.subServices.length}
                      </span>
                    </Link>
                  );
                }

                if (cat.id === "branding-creatives-detailed") {
                  return (
                    <Link
                      key={cat.id}
                      to="/services/branding-creatives"
                      className={`flex items-center justify-center gap-2.5 p-3.5 rounded-2xl border text-xs sm:text-sm font-bold transition-all duration-300 cursor-pointer ${
                        isCatActive
                          ? "bg-pink-500/20 text-white border-pink-500/40 shadow-[0_0_20px_rgba(236,72,153,0.3)] ring-1 ring-pink-500/30"
                          : "bg-white/[0.03] text-muted-foreground hover:text-white hover:bg-white/[0.08] border-white/5"
                      }`}
                    >
                      <CatIcon className="h-4 w-4 text-pink-400" />
                      <span className="truncate">{cat.title}</span>
                      <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-pink-500/20 text-pink-300 border border-pink-500/30">
                        {cat.subServices.length}
                      </span>
                    </Link>
                  );
                }

                return (
                  <button
                    key={cat.id}
                    onClick={() => {
                      setActiveDetailCatId(cat.id);
                      if (cat.subServices.length > 0) {
                        setActiveSubServiceId(cat.subServices[0].id);
                      }
                    }}
                    className={`flex items-center justify-center gap-2.5 p-3.5 rounded-2xl border text-xs sm:text-sm font-bold transition-all duration-300 cursor-pointer ${
                      isCatActive
                        ? "bg-white/15 text-white border-white/30 shadow-[0_0_20px_rgba(255,255,255,0.15)] ring-1 ring-white/20"
                        : "bg-white/[0.03] text-muted-foreground hover:text-white hover:bg-white/[0.08] border-white/5"
                    }`}
                  >
                    <CatIcon className={`h-4 w-4 ${isCatActive ? "text-[var(--glow-orange)]" : "text-muted-foreground"}`} />
                    <span className="truncate">{cat.title}</span>
                    <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-white/10 text-white/80">
                      {cat.subServices.length}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Sub-Services Navigation & Showcase Container */}
            <div className="rounded-3xl glass-strong border border-white/15 p-6 sm:p-10 md:p-12 relative overflow-hidden shadow-2xl">
              <div
                className="absolute -top-32 -right-32 h-96 w-96 rounded-full blur-[140px] opacity-15 pointer-events-none"
                style={{ backgroundColor: activeDetailCategory.bgGlow }}
              />

              <div className="grid lg:grid-cols-12 gap-8 items-start relative z-10">
                {/* Left Column: Sub-Services List */}
                <div className="lg:col-span-5 bg-[#06060c]/90 rounded-2xl p-4 sm:p-5 border border-white/10 shadow-inner flex flex-col max-h-[520px] sm:max-h-[560px] overflow-hidden">
                  <div className="flex items-center justify-between border-b border-white/10 pb-3 mb-3.5 shrink-0">
                    <span className="text-xs font-mono uppercase tracking-widest text-white/80 flex items-center gap-2">
                      <ActiveSubIcon className="h-3.5 w-3.5 text-[var(--glow-orange)]" />
                      {activeDetailCategory.title} Options
                    </span>
                    <span className="text-xs font-mono text-muted-foreground">
                      {activeDetailCategory.subServices.length} Channels
                    </span>
                  </div>

                  <div
                    ref={subServicesScrollRef}
                    onMouseEnter={() => setIsSubScrollHovered(true)}
                    onMouseLeave={() => setIsSubScrollHovered(false)}
                    onTouchStart={() => setIsSubScrollHovered(true)}
                    onTouchEnd={() => setIsSubScrollHovered(false)}
                    className="space-y-2 overflow-y-auto pr-1.5 pb-6 flex-1 custom-scrollbar"
                  >
                    {activeDetailCategory.subServices.map((sub) => {
                      const SubIcon = sub.icon;
                      const isSubSelected = activeSubServiceId === sub.id;
                      return (
                        <button
                          key={sub.id}
                          onClick={() => setActiveSubServiceId(sub.id)}
                          className={`w-full text-left flex items-start sm:items-center justify-between p-3 rounded-xl border text-xs sm:text-sm font-medium transition duration-300 cursor-pointer ${
                            isSubSelected
                              ? "bg-[var(--glow-orange)]/20 text-white border-[var(--glow-orange)]/40 shadow-md"
                              : "bg-white/[0.02] text-muted-foreground hover:text-white hover:bg-white/[0.06] border-white/5"
                          }`}
                        >
                          <div className="flex items-start sm:items-center gap-2.5 min-w-0 pr-2">
                            <SubIcon className={`h-4 w-4 shrink-0 mt-0.5 sm:mt-0 ${isSubSelected ? "text-[var(--glow-orange)]" : "text-muted-foreground"}`} />
                            <span className="break-words leading-snug whitespace-normal">{sub.name}</span>
                          </div>
                          <ChevronRight className={`h-3.5 w-3.5 shrink-0 mt-0.5 sm:mt-0 transition ${isSubSelected ? "text-[var(--glow-orange)] translate-x-1" : "text-muted-foreground"}`} />
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Right Column: Selected Sub-Service Detailed Showcase */}
                <div className="lg:col-span-7 space-y-6">
                  <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-mono tracking-wider border bg-white/5 border-white/10 text-white/90">
                    <ActiveSubIcon className="h-3.5 w-3.5 text-[var(--glow-orange)]" />
                    {activeDetailCategory.title} · {activeSubService.name}
                  </div>

                  <div>
                    <h3 className="font-display text-2xl sm:text-3xl font-bold text-white leading-tight">
                      {activeSubService.name}
                    </h3>
                    <p className="mt-2 text-base font-accent italic text-white/90 border-l-2 border-[var(--glow-orange)] pl-4 py-0.5">
                      "{activeSubService.tagline}"
                    </p>
                  </div>

                  <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                    {activeSubService.desc}
                  </p>

                  <div className="pt-2">
                    <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground block mb-3">
                      Key Deliverables & Execution Tactics:
                    </span>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                      {activeSubService.deliverables.map((del, dIdx) => (
                        <div
                          key={dIdx}
                          className="flex items-center gap-2.5 p-3 rounded-xl bg-white/[0.04] border border-white/10 text-xs font-medium text-white/90"
                        >
                          <Check className="h-4 w-4 text-emerald-400 shrink-0" />
                          <span>{del}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="pt-4 flex flex-wrap gap-4">
                    {activeSubService.id === "tv-detailed" || activeSubService.name === "Television" ? (
                      <Link to="/services/television" className="btn-hero !py-3 !px-6 !text-xs sm:!text-sm inline-flex items-center gap-2">
                        Explore Television Advertising
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    ) : activeSubService.id === "print-detailed" || activeSubService.name === "Print" ? (
                      <Link to="/services/print" className="btn-hero !py-3 !px-6 !text-xs sm:!text-sm inline-flex items-center gap-2">
                        Explore Print Advertising
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    ) : activeSubService.id === "radio-detailed" || activeSubService.name === "Radio" ? (
                      <Link to="/services/radio" className="btn-hero !py-3 !px-6 !text-xs sm:!text-sm inline-flex items-center gap-2">
                        Explore Radio Advertising
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    ) : activeSubService.id === "ooh-detailed" || activeSubService.name.includes("Outdoor") ? (
                      <Link to="/services/outdoor" className="btn-hero !py-3 !px-6 !text-xs sm:!text-sm inline-flex items-center gap-2">
                        Explore Outdoor (OOH) Advertising
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    ) : activeSubService.id === "cinema-detailed" || activeSubService.name.includes("Cinema") ? (
                      <Link to="/services/cinema" className="btn-hero !py-3 !px-6 !text-xs sm:!text-sm inline-flex items-center gap-2">
                        Explore Cinema Advertising
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    ) : activeSubService.id === "performance-detailed" || activeSubService.name.includes("Performance") ? (
                      <Link to="/services/performance-marketing" className="btn-hero !py-3 !px-6 !text-xs sm:!text-sm inline-flex items-center gap-2">
                        Explore Performance Marketing
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    ) : activeSubService.id === "display-detailed" || activeSubService.name.includes("Display") || activeSubService.name.includes("Native") ? (
                      <Link to="/services/display-native-ads" className="btn-hero !py-3 !px-6 !text-xs sm:!text-sm inline-flex items-center gap-2">
                        Explore Display & Native Ads
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    ) : activeSubService.id === "ott-detailed" || activeSubService.name.includes("OTT") ? (
                      <Link to="/services/ott-advertising" className="btn-hero !py-3 !px-6 !text-xs sm:!text-sm inline-flex items-center gap-2">
                        Explore OTT Advertising
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    ) : activeSubService.id === "app-detailed" || activeSubService.name.includes("App") ? (
                      <Link to="/services/app-advertising" className="btn-hero !py-3 !px-6 !text-xs sm:!text-sm inline-flex items-center gap-2">
                        Explore App Advertising
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    ) : activeSubService.id === "programmatic-detailed" || activeSubService.name.includes("Programmatic") ? (
                      <Link to="/services/programmatic-advertising" className="btn-hero !py-3 !px-6 !text-xs sm:!text-sm inline-flex items-center gap-2">
                        Explore Programmatic Advertising
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    ) : activeSubService.id === "seo-detailed" || activeSubService.name === "SEO" ? (
                      <Link to="/services/seo" className="btn-hero !py-3 !px-6 !text-xs sm:!text-sm inline-flex items-center gap-2">
                        Explore SEO Services
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    ) : activeSubService.id === "orm-detailed" || activeSubService.name.includes("ORM") || activeSubService.name.includes("Reputation") ? (
                      <Link to="/services/orm" className="btn-hero !py-3 !px-6 !text-xs sm:!text-sm inline-flex items-center gap-2">
                        Explore ORM Services
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    ) : activeSubService.id === "smm-detailed" || activeSubService.name.includes("Social Media") ? (
                      <Link to="/services/social-media-marketing" className="btn-hero !py-3 !px-6 !text-xs sm:!text-sm inline-flex items-center gap-2">
                        Explore Social Media Marketing
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    ) : activeSubService.id === "ai-detailed" || activeSubService.name.includes("AI Marketing") ? (
                      <Link to="/services/ai-marketing" className="btn-hero !py-3 !px-6 !text-xs sm:!text-sm inline-flex items-center gap-2">
                        Explore AI Marketing
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    ) : activeSubService.id === "web-detailed" || activeSubService.name.includes("Website") || activeSubService.name.includes("Web Development") ? (
                      <Link to="/services/web-development" className="btn-hero !py-3 !px-6 !text-xs sm:!text-sm inline-flex items-center gap-2">
                        Explore Web Design & Development
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    ) : activeSubService.id === "email-detailed" || activeSubService.name.includes("Email") || activeSubService.name.includes("Marketing Automation") || activeSubService.name.includes("CRM") ? (
                      <Link to="/services/email-marketing" className="btn-hero !py-3 !px-6 !text-xs sm:!text-sm inline-flex items-center gap-2">
                        Explore Email Marketing
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    ) : activeSubService.id === "analytics-detailed" || activeSubService.name.includes("Analytics") ? (
                      <Link to="/services/analytics" className="btn-hero !py-3 !px-6 !text-xs sm:!text-sm inline-flex items-center gap-2">
                        Explore Analytics
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    ) : activeSubService.id === "cro-detailed" || activeSubService.name.includes("Conversion") || activeSubService.name.includes("CRO") ? (
                      <Link to="/services/cro" className="btn-hero !py-3 !px-6 !text-xs sm:!text-sm inline-flex items-center gap-2">
                        Explore Conversion Optimisation
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    ) : activeSubService.id === "data-detailed" || activeSubService.name.includes("Data Intelligence") ? (
                      <Link to="/services/data-intelligence" className="btn-hero !py-3 !px-6 !text-xs sm:!text-sm inline-flex items-center gap-2">
                        Explore Data Intelligence
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    ) : activeSubService.id === "logo-sub" || activeSubService.name.includes("Logo Design") ? (
                      <Link to="/services/logo-design" className="btn-hero !py-3 !px-6 !text-xs sm:!text-sm inline-flex items-center gap-2">
                        Explore Logo Design Services
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    ) : activeSubService.id === "visual-sub" || activeSubService.name.includes("Visual Identity") ? (
                      <Link to="/services/visual-identity" className="btn-hero !py-3 !px-6 !text-xs sm:!text-sm inline-flex items-center gap-2">
                        Explore Visual Identity Services
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    ) : activeSubService.id === "motion-sub" || activeSubService.name.includes("Motion Graphics") ? (
                      <Link to="/services/motion-graphics" className="btn-hero !py-3 !px-6 !text-xs sm:!text-sm inline-flex items-center gap-2">
                        Explore Motion Graphics Services
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    ) : activeSubService.id === "ai-video-sub" || activeSubService.name.includes("AI Video") ? (
                      <Link to="/services/ai-video-creation" className="btn-hero !py-3 !px-6 !text-xs sm:!text-sm inline-flex items-center gap-2">
                        Explore AI Video Creation Services
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    ) : activeSubService.id === "video-prod-sub" || activeSubService.name.includes("Video Production") ? (
                      <Link to="/services/video-production" className="btn-hero !py-3 !px-6 !text-xs sm:!text-sm inline-flex items-center gap-2">
                        Explore Video Production Services
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    ) : activeSubService.id === "photo-sub" || activeSubService.name.includes("Photography") ? (
                      <Link to="/services/photography" className="btn-hero !py-3 !px-6 !text-xs sm:!text-sm inline-flex items-center gap-2">
                        Explore Photography Services
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    ) : activeSubService.id === "copywriting-sub" || activeSubService.name.includes("Copywriting") ? (
                      <Link to="/services/copywriting" className="btn-hero !py-3 !px-6 !text-xs sm:!text-sm inline-flex items-center gap-2">
                        Explore Copywriting Services
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    ) : activeSubService.id === "campaign-sub" || activeSubService.name.includes("Campaign Design") ? (
                      <Link to="/services/campaign-design" className="btn-hero !py-3 !px-6 !text-xs sm:!text-sm inline-flex items-center gap-2">
                        Explore Campaign Design Services
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    ) : activeSubService.id === "office-sub" || activeSubService.name.includes("Corporate Office Branding") ? (
                      <Link to="/services/corporate-office-branding" className="btn-hero !py-3 !px-6 !text-xs sm:!text-sm inline-flex items-center gap-2">
                        Explore Corporate Office Branding Services
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    ) : activeSubService.id?.includes("branding") || activeCategory.id === "branding-creatives-detailed" || activeSubService.name.includes("Branding") || activeSubService.name.includes("Logo") || activeSubService.name.includes("Visual") || activeSubService.name.includes("Motion") || activeSubService.name.includes("Photography") || activeSubService.name.includes("Copywriting") || activeSubService.name.includes("Campaign") ? (
                      <Link to="/services/branding-creatives" className="btn-hero !py-3 !px-6 !text-xs sm:!text-sm inline-flex items-center gap-2">
                        Explore Branding & Creative Services
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    ) : activeSubService.id?.includes("audit") || activeCategory.id === "research-consulting-detailed" || activeSubService.name.includes("Audit") || activeSubService.name.includes("Research") ? (
                      <Link to="/services/research-consulting" className="btn-hero !py-3 !px-6 !text-xs sm:!text-sm inline-flex items-center gap-2">
                        Explore Research & Consulting Services
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    ) : (
                      <a href="/contact-us" className="btn-hero !py-3 !px-6 !text-xs sm:!text-sm inline-flex items-center gap-2">
                        Inquire About {activeSubService.name.split(" ")[0]}
                        <ArrowRight className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* BRANDING & CREATIVES + RESEARCH & CONSULTING FEATURED SECTION */}
        <section className="py-16 md:py-24 border-b border-white/10 relative overflow-hidden bg-white/[0.01]">
          <div className="container-x relative z-10">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-orange)] block mb-2">
                Creative & Advisory Capabilities
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
                Branding, Creatives & Strategic Research
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground mt-3 leading-relaxed">
                From iconic visual identities and cinematic AI video production to independent rate benchmarking and campaign audits.
              </p>
            </div>

            <div className="grid lg:grid-cols-12 gap-8">
              {/* Branding & Creatives Card (Spans 7 cols) */}
              <div className="lg:col-span-7 p-8 sm:p-10 rounded-3xl glass-strong border border-white/15 relative overflow-hidden space-y-6 flex flex-col justify-between">
                <div className="glow-orb -left-20 -top-20 h-64 w-64 bg-pink-500 opacity-15" />
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-mono tracking-wider border bg-white/5 border-white/10 text-pink-400">
                      <Sparkles className="h-3.5 w-3.5" />
                      Branding & Creatives
                    </div>
                    <span className="text-xs font-mono text-muted-foreground">9 Services</span>
                  </div>

                  <h3 className="font-display text-2xl sm:text-3xl font-bold text-white mb-2">
                    Build Strong Brand Equity & High-Impact Creative Assets
                  </h3>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    End-to-end brand identity design, motion graphics, video shoots, and AI-powered creative synthesis engineered to stand out across print, digital, TV, and social.
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-6">
                    {[
                      { name: "Branding", icon: Sparkles, tag: "Brand Identity & Positioning" },
                      { name: "Logo Design", icon: PenTool, tag: "Iconic Vector Logo Marks" },
                      { name: "Visual Identity", icon: Brush, tag: "Color, Type & Style Systems" },
                      { name: "Motion Graphics", icon: Zap, tag: "2D/3D Animation & Reveals" },
                      { name: "AI Video Creation (Long-form & Short-form)", icon: Wand2, tag: "Rapid AI Reel & Ad Synthesis" },
                      { name: "Video Production", icon: Video, tag: "Cinematic Shoots & Editing" },
                      { name: "Photography", icon: Camera, tag: "Product & Corporate Shoots" },
                      { name: "Copywriting", icon: FileText, tag: "Direct-Response Ad Copy" },
                      { name: "Campaign Design", icon: Layout, tag: "360° Key Visuals (KV)" },
                    ].map((item, idx) => {
                      const BIcon = item.icon;
                      return (
                        <button
                          key={idx}
                          onClick={() => {
                            setActiveDetailCatId("branding-creatives-detailed");
                            const sub = detailedCategories[2]?.subServices.find((s) => s.name === item.name);
                            if (sub) setActiveSubServiceId(sub.id);
                            const el = document.getElementById("detailed-breakdown-section");
                            if (el) el.scrollIntoView({ behavior: "smooth" });
                          }}
                          className="flex items-start gap-3 p-3 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-pink-500/40 hover:bg-white/[0.08] transition text-left group cursor-pointer"
                        >
                          <div className="h-8 w-8 rounded-lg bg-pink-500/10 border border-pink-500/20 flex items-center justify-center text-pink-400 shrink-0 mt-0.5 group-hover:scale-110 transition-transform">
                            <BIcon className="h-4 w-4" />
                          </div>
                          <div>
                            <span className="text-xs font-bold text-white group-hover:text-pink-300 transition block">
                              {item.name}
                            </span>
                            <span className="text-[10px] text-muted-foreground block line-clamp-1">
                              {item.tag}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="pt-4 border-t border-white/10 flex items-center justify-between">
                  <span className="text-xs font-mono text-muted-foreground">Full Creative Suite</span>
                  <button
                    onClick={() => {
                      setActiveDetailCatId("branding-creatives-detailed");
                      const el = document.getElementById("detailed-breakdown-section");
                      if (el) el.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="text-xs font-mono font-bold text-pink-400 hover:text-white flex items-center gap-1 transition cursor-pointer"
                  >
                    Explore Branding Details
                    <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>

              {/* Research & Consulting Card (Spans 5 cols) */}
              <div className="lg:col-span-5 p-8 sm:p-10 rounded-3xl glass-strong border border-white/15 relative overflow-hidden space-y-6 flex flex-col justify-between bg-[#06060c]/90">
                <div className="glow-orb -right-20 -top-20 h-64 w-64 bg-emerald-500 opacity-15" />
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-mono tracking-wider border bg-white/5 border-white/10 text-emerald-400">
                      <BarChart3 className="h-3.5 w-3.5" />
                      Research & Consulting
                    </div>
                    <span className="text-xs font-mono text-muted-foreground">3 Audits</span>
                  </div>

                  <h3 className="font-display text-2xl font-bold text-white mb-2">
                    Independent Audits & Strategic Growth Roadmaps
                  </h3>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    Verify media buying efficiency, audit brand health consistency, and uncover funnel conversion leaks with independent data evaluation.
                  </p>

                  <div className="space-y-3 pt-6">
                    {[
                      {
                        name: "Media Audit",
                        desc: "Independent media buying efficiency, rate card benchmarking & spend waste reduction.",
                        icon: FileCheck,
                      },
                      {
                        name: "Brand Audit",
                        desc: "360° brand health, competitor positioning & touchpoint consistency review.",
                        icon: UserCheck,
                      },
                      {
                        name: "Campaign Audit",
                        desc: "Deep-dive multi-touch attribution, ad fatigue & funnel leak identification.",
                        icon: PieChart,
                      },
                    ].map((audit, aIdx) => {
                      const AIcon = audit.icon;
                      return (
                        <button
                          key={aIdx}
                          onClick={() => {
                            setActiveDetailCatId("research-consulting-detailed");
                            const sub = detailedCategories[3]?.subServices.find((s) => s.name === audit.name);
                            if (sub) setActiveSubServiceId(sub.id);
                            const el = document.getElementById("detailed-breakdown-section");
                            if (el) el.scrollIntoView({ behavior: "smooth" });
                          }}
                          className="w-full flex items-start gap-3.5 p-4 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-emerald-500/40 hover:bg-white/[0.08] transition text-left group cursor-pointer"
                        >
                          <div className="h-9 w-9 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 shrink-0 group-hover:scale-110 transition-transform">
                            <AIcon className="h-4.5 w-4.5" />
                          </div>
                          <div>
                            <span className="text-sm font-bold text-white group-hover:text-emerald-300 transition block">
                              {audit.name}
                            </span>
                            <span className="text-xs text-muted-foreground leading-relaxed block mt-0.5">
                              {audit.desc}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="pt-4 border-t border-white/10 flex items-center justify-between">
                  <span className="text-xs font-mono text-muted-foreground">Strategic Consulting</span>
                  <button
                    onClick={() => {
                      setActiveDetailCatId("research-consulting-detailed");
                      const el = document.getElementById("detailed-breakdown-section");
                      if (el) el.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="text-xs font-mono font-bold text-emerald-400 hover:text-white flex items-center gap-1 transition cursor-pointer"
                  >
                    Explore Audits Details
                    <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* WHY CHOOSE KMWA VALUE PROPOSITION SECTION */}
        <section className="py-16 md:py-24 border-b border-white/10 relative overflow-hidden">
          <div className="container-x relative z-10">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-orange)] block mb-2">
                Strategic Advantage
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
                Bringing Value to Your Brand
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground mt-3">
                Why global enterprises and fast-growing brands partner with KMWA Advertising for full-funnel media and performance execution.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {valueProps.map((vp, idx) => {
                const VpIcon = vp.icon;
                return (
                  <div
                    key={idx}
                    className="p-6 sm:p-8 rounded-2xl glass-strong border border-white/10 hover:border-white/20 transition duration-300 flex flex-col justify-between group"
                  >
                    <div>
                      <div className="flex items-center justify-between mb-6">
                        <span className="font-mono text-2xl font-bold text-white/30 group-hover:text-[var(--glow-orange)] transition-colors">
                          {vp.num}
                        </span>
                        <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white">
                          <VpIcon className="h-5 w-5" />
                        </div>
                      </div>
                      <h3 className="font-display text-lg font-bold text-white mb-2 leading-snug">
                        {vp.title}
                      </h3>
                      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                        {vp.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CLOSING STRATEGIC ADVISORY CTA */}
        <section className="py-20 md:py-28 relative overflow-hidden">
          <div className="glow-orb left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[500px] bg-[var(--glow-orange)] opacity-20" />

          <div className="container-x relative z-10 text-center max-w-4xl">
            <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs uppercase tracking-widest text-muted-foreground border border-white/10 mb-6">
              <Sparkles className="h-3.5 w-3.5 text-[var(--glow-orange)]" />
              Tailored Growth Roadmaps
            </div>

            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight">
              Ready to Transform Your Media & Marketing Strategy?
            </h2>

            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Every brand's growth trajectory is unique. Let's design a integrated campaign strategy engineered for mass awareness, high conversion, and maximum ROAS.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="/contact-us" className="btn-hero w-full sm:w-auto">
                Request Custom Proposal
                <ArrowRight className="h-4 w-4" />
              </a>
              <Link to="/about" className="btn-ghost w-full sm:w-auto">
                Learn About KMWA
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
